import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Chip, Stack
} from '@mui/material';

interface BlocModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: { title: string; order: number; imageUrl: string; certificationNumber: string; competencies: string[] }) => void;
    initialData?: { title: string; order: number; imageUrl?: string; certificationNumber?: string; competencies?: string[] };
    isEditing?: boolean;
}

const BlocModal: React.FC<BlocModalProps> = ({ open, onClose, onSave, initialData, isEditing }) => {
    const [title, setTitle] = useState('');
    const [order, setOrder] = useState(0);
    const [imageUrl, setImageUrl] = useState('');
    const [certificationNumber, setCertificationNumber] = useState('');
    const [competencies, setCompetencies] = useState<string[]>([]);
    const [competencyInput, setCompetencyInput] = useState('');
    const [titleError, setTitleError] = useState('');

    // Reset state when opening
    useEffect(() => {
        if (open) {
            if (isEditing && initialData) {
                setTitle(initialData.title);
                setOrder(initialData.order);
                setImageUrl(initialData.imageUrl || '');
                setCertificationNumber(initialData.certificationNumber || '');
                setCompetencies(initialData.competencies || []);
            } else {
                setTitle('');
                setOrder(0);
                setImageUrl('');
                setCertificationNumber('');
                setCompetencies([]);
            }
            setCompetencyInput('');
            setTitleError('');
        }
    }, [open, initialData, isEditing]);

    const handleAddCompetency = () => {
        if (competencyInput.trim()) {
            setCompetencies([...competencies, competencyInput.trim()]);
            setCompetencyInput('');
        }
    };

    const handleDeleteCompetency = (compToDelete: string) => {
        setCompetencies(competencies.filter((comp) => comp !== compToDelete));
    };

    const handleSave = () => {
        if (!title.trim()) {
            setTitleError('Le titre est requis');
            return;
        }
        onSave({ title, order, imageUrl, certificationNumber, competencies });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Modifier le bloc' : 'Ajouter un nouveau bloc'}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Titre du bloc"
                    fullWidth
                    required
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        if (titleError) setTitleError('');
                    }}
                    error={!!titleError}
                    helperText={titleError}
                />
                <TextField
                    margin="dense"
                    label="Order Number"
                    type="number"
                    fullWidth
                    value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                />
                <TextField
                    margin="dense"
                    label="Image URL (Optional)"
                    fullWidth
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                />
                <TextField
                    margin="dense"
                    label="Certification Number (Optional)"
                    fullWidth
                    value={certificationNumber}
                    onChange={(e) => setCertificationNumber(e.target.value)}
                />

                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            label="Add Competency"
                            fullWidth
                            size="small"
                            value={competencyInput}
                            onChange={(e) => setCompetencyInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCompetency()}
                        />
                        <Button onClick={handleAddCompetency} variant="outlined">Add</Button>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        {competencies.map((comp, index) => (
                            <Chip
                                key={index}
                                label={comp}
                                onDelete={() => handleDeleteCompetency(comp)}
                            />
                        ))}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">{isEditing ? 'Save Changes' : 'Create'}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BlocModal;
